import React, { useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';

/**
 * CollegeRateByDBChart
 * - Horizontal stacked bar chart showing SCI, Scopus, KCI rates by college.
 * - records: from parent (e.g. /rm/v1/stats/college_stats). rate_by_college_chart 숏코드와 동일 형식.
 */
const collegeLabels = [
  "간호대학", "경영대학", "경제통상대학", "공과대학", "국제전문대학원", "나노과학기술대학",
  "데이터사이언스전문대학원", "법학전문대학원", "사범대학", "사회과학대학",
  "생명자원과학대학", "생활과학대학", "약학대학", "예술대학", "의과대학", "인문대학",
  "자연과학대학", "정보의생명공학대학", "치의학전문대학원", "한의학전문대학원"
];

export default function CollegeRateByDBChart({ records = [] }) {
  const apiData = Array.isArray(records) ? records : [];

  const items = useMemo(() => {
    if (!apiData.length) return [];

    const dataMap = {};
    apiData.forEach((record) => {
      if (record.col_name) {
        dataMap[record.col_name] = {
          wos_ratio: parseFloat(record.wos_ratio || 0),
          scopus_ratio: parseFloat(record.scopus_ratio || 0),
          kci_ratio: parseFloat(record.kci_ratio || 0)
        };
      }
    });

    const result = [];
    collegeLabels.forEach((label) => {
      const data = dataMap[label] || { wos_ratio: 0, scopus_ratio: 0, kci_ratio: 0 };
      let wos = data.wos_ratio;
      let scopus = data.scopus_ratio;
      let kci = data.kci_ratio;
      const total = wos + scopus + kci;
      if (total > 100) kci = kci - (total - 100);
      else if (total < 100) kci = kci + (100 - total);
      result.push({ label, sci: wos, scopus: scopus, kci: kci });
    });
    return result;
  }, [records]);

  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const draw = () => {
      el.innerHTML = '';

      if (!Array.isArray(items) || items.length === 0) {
        return;
      }

      // rate_by_college_chart.php와 동일: x축 0~100% 기준. 컨테이너 너비만 사용해 잘림 방지
      const rawWidth = el.clientWidth || 0;
      if (rawWidth < 200) return; // 너무 좁으면 그리지 않음 (리사이즈 시 다시 그림)
      const width = rawWidth;
      const barHeight = 10;
      const barGap = 4;

      const dataCount = items.length;
      let labelColWidth;
      // x축 라벨(0%~100%)이 차트 밖으로 잘리지 않도록 오른쪽 여백 충분히 확보 (lg 2열 레이아웃 대응)
      const rightMargin = 72;
      if (dataCount <= 8) {
        labelColWidth = width * 0.20;
      } else if (dataCount <= 15) {
        labelColWidth = width * 0.25;
      } else {
        labelColWidth = width * 0.18;
      }

      const top = 8;
      const bottom = 30;
      const margin = { top, right: rightMargin, bottom, left: labelColWidth };

      const n = items.length;
      const innerH = n > 0 ? n * barHeight + (n - 1) * barGap : 0;
      const height = innerH + top + bottom;

      const svg = d3
        .select(el)
        .append('svg')
        .attr('width', '100%')
        .attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('preserveAspectRatio', 'xMidYMin meet')
        .style('max-width', '100%')
        .style('overflow', 'hidden');

      const innerW = Math.max(0, width - margin.left - margin.right);

      // 플롯 그룹 (오른쪽 차트 50% 영역)
      const g = svg
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const max = 100;
      // x축 범위를 innerW에 정확히 맞춤 (오른쪽 여백 제거)
      const x = d3.scaleLinear().domain([0, max]).range([0, innerW]).clamp(false);
      const yPos = (index) => index * (barHeight + barGap);

      // 세로 그리드
      const gridVals = [0, 20, 40, 60, 80, 100];
      g.append('g')
        .selectAll('line')
        .data(gridVals)
        .enter()
        .append('line')
        .attr('x1', (d) => x(d))
        .attr('x2', (d) => x(d))
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#E4E8F1');

      // x축: 0%, 20%, 40%, 60%, 80%, 100% (rate_by_college_chart.php scales.x 동일: min 0, max 100, ticks callback value + '%')
      const xAxisG = g.append('g').attr('transform', `translate(0,${innerH})`);
      xAxisG.call(
        d3
          .axisBottom(x)
          .tickValues(gridVals)
          .tickFormat((v) => String(v) + '%')
      );
      xAxisG.selectAll('.domain').remove();
      xAxisG.selectAll('.tick line').remove();
      // x축 라벨이 innerW 영역 안에 들어가도록: 마지막 틱(100%)은 innerW 기준 end 정렬
      xAxisG.selectAll('.tick text')
        .attr('fill', '#6C7687')
        .style('font-size', 10)
        .text((d) => String(d) + '%')
        .attr('x', 0)       
        .attr('dx', 0)
        .attr('text-anchor', 'middle');

      // 스택 데이터 준비
      const stackData = items.map((d) => {
        const sci = Math.max(0, Math.min(100, Number(d.sci) || 0));
        const scopus = Math.max(0, Math.min(100, Math.min(100 - sci, Number(d.scopus) || 0)));
        const kci = Math.max(0, Math.min(100 - sci - scopus, Number(d.kci) || 0));
        // 총합이 100이 되도록 조정
        const total = sci + scopus + kci;
        const adjustedSci = total > 0 ? (sci / total) * 100 : 0;
        const adjustedScopus = total > 0 ? (scopus / total) * 100 : 0;
        const adjustedKci = 100 - adjustedSci - adjustedScopus;
        return [adjustedSci, adjustedScopus, adjustedKci];
      });

      // Tooltip 생성
      d3.selectAll('.college-rate-tooltip').remove();
      const tooltip = d3.select('body')
        .append('div')
        .attr('class', 'college-rate-tooltip')
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.85)')
        .style('color', 'white')
        .style('padding', '8px 12px')
        .style('border-radius', '6px')
        .style('font-size', '13px')
        .style('font-weight', '600')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', 10000)
        .style('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.3)');

      // 색상 정의 (PHP 코드와 동일하게)
      const colors = ['#B298F2', '#FFBE8D', '#5FA3E0']; // SCI (파란색), Scopus (주황색), KCI (초록색)
      const colorLabels = ['SCIs', 'Scopus', 'KCI'];

      // 스택 레이어 생성
      const layers = d3.stack().keys([0, 1, 2])(stackData);

      // 각 레이어 그리기 (숏코드 Chart.js 옵션 참고 - 각진 모서리)
      layers.forEach((layer, layerIndex) => {
        g.append('g')
          .selectAll('rect')
          .data(layer)
          .enter()
          .append('rect')
          .attr('x', (d) => x(d[0]))
          .attr('y', (_, i) => yPos(i))
          .attr('height', barHeight)
          .attr('fill', colors[layerIndex])
          .style('cursor', 'pointer')
          .attr('width', (d) => {
            const width = x(d[1]) - x(d[0]);
            return Math.max(0, width);
          })
          .attr('rx', 0) // 각진 모서리
          .attr('ry', 0)
          .on('mouseenter', function(event, d, i) {
            // i는 현재 레이어에서의 인덱스 = items 배열의 인덱스
            const item = items[i];
            const label = item?.label || '';
            const value = d[1] - d[0]; // 해당 세그먼트의 값 (퍼센트)
            const colorLabel = colorLabels[layerIndex];
            
            tooltip
              .html(`<div>${label}</div><div style="margin-top:4px;font-size:11px;opacity:0.9;"><span style="color:${colors[layerIndex]}">${colorLabel}</span>: ${value.toFixed(1)}%</div>`)
              .style('opacity', 1);
          })
          .on('mousemove', function(event) {
            tooltip
              .style('left', (event.pageX + 10) + 'px')
              .style('top', (event.pageY - 10) + 'px');
          })
          .on('mouseleave', function() {
            tooltip.style('opacity', 0);
          });
      });

      // 왼쪽 대학 라벨들
      const labelsGroup = svg
        .append('g')
        .attr('transform', `translate(${labelColWidth - 8},${margin.top})`);

      labelsGroup
        .selectAll('text')
        .data(items)
        .enter()
        .append('text')
        .attr('x', 0)
        .attr('y', (_, i) => yPos(i) + barHeight / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('fill', '#6C7687')
        .style('font-size', 11) // 9에서 11로 키움
        .text((d) => d.label);
    };

    draw();

    let ro;
    try {
      if ('ResizeObserver' in window) {
        ro = new ResizeObserver(() => draw());
        ro.observe(ref.current);
      } else {
        window.addEventListener('resize', draw);
      }
    } catch {
      window.addEventListener('resize', draw);
    }

    return () => {
      try {
        if (ro) ro.disconnect();
        else window.removeEventListener('resize', draw);
        // cleanup: tooltip 제거
        d3.selectAll('.college-rate-tooltip').remove();
      } catch {
        // ignore
      }
    };
  }, [items]);

  return (
    <div className="relative w-full" style={{ minWidth: 0, overflow: 'hidden' }}>
      {/* rate_by_college_chart.php와 동일: x축 min 0, max 100, 0~100% 표시. 컨테이너 안에 맞춤 */}
      <div style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
        <div ref={ref} className="w-full" style={{ minWidth: 0, maxWidth: '100%' }} />
      </div>

      {/* 범례 (하단에 표시) */}
      <div className="mt-4 flex items-center justify-center gap-6">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-[#B298F2]"></span>
          <span className="text-base text-[#6C7687]">SCIs</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-[#FFBE8D]"></span>
          <span className="text-base text-[#6C7687]">Scopus</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-[#5FA3E0]"></span>
          <span className="text-base text-[#6C7687]">KCI</span>
        </div>
      </div>

    </div>
  );
}
